"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { SectionReveal } from "./SectionReveal";
import { Phone, Mail, MapPin } from "lucide-react";

const QUICK_LINKS = [
  { label: "Home", href: "/" },
  { label: "Offers", href: "/offers" },
  { label: "Fleet", href: "/fleet" },
  { label: "About Us", href: "/about-us" },
  { label: "Contact Us", href: "/contact" },
];

const SUPPORT_LINKS = [
  { label: "FAQ", href: "/faq" },
  { label: "Cancellation & Refund", href: "/cancellation-and-refund" },
  { label: "Terms & Conditions", href: "/terms-and-conditions" },
  { label: "Privacy Policy", href: "/privacy-policy" },
];

const SOCIAL_LINKS = [
  { label: "Facebook", href: "#", icon: "facebook" },
  { label: "Twitter", href: "#", icon: "twitter" },
  { label: "Instagram", href: "#", icon: "instagram" },
  { label: "LinkedIn", href: "#", icon: "linkedin" },
];

export function IKFooter() {
  return (
    <SectionReveal
      as="footer"
      className="bg-[#fff8f0] dark:bg-neutral-900/80 border-t border-[#FFB51D]/10 dark:border-neutral-800"
      aria-label="Site footer"
    >
      <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Column 1 - Brand */}
          <div className="lg:col-span-1">
            <Link href="/" className="inline-flex items-center gap-2 mb-3">
              <Image
                src="/images/resources/logo-1.png"
                alt="I K TOURS AND TRAVELS"
                width={140}
                height={40}
                className="h-8 w-auto"
              />
            </Link>
            <h2 className="text-base font-bold text-[#131222] dark:text-white mb-2">
              I K TOURS AND TRAVELS
            </h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6 leading-relaxed">
              Legacy In Every Mile.
              <br />
              Serving Since 1985
            </p>
            <div className="flex gap-2">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] text-[#131222] dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                  aria-label={social.label}
                >
                  <SocialIcon name={social.icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h2 className="text-base font-bold text-[#131222] dark:text-white mb-4">
              Quick Links
            </h2>
            <ul className="space-y-3 list-none p-0 m-0">
              {QUICK_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 - Policies */}
          <div>
            <h2 className="text-base font-bold text-[#131222] dark:text-white mb-4">
              Policies
            </h2>
            <ul className="space-y-3 list-none p-0 m-0">
              {SUPPORT_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 - Contact */}
          <div>
            <h2 className="text-base font-bold text-[#131222] dark:text-white mb-4">
              Contact Us
            </h2>
            <ul className="space-y-4 list-none p-0 m-0">
              <li className="flex items-start gap-3">
                <Phone
                  className="w-5 h-5 shrink-0 text-[#131222] dark:text-neutral-400 mt-0.5"
                  aria-hidden
                />
                <a
                  href="tel:+919809548899"
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                >
                  +91 9809548899
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail
                  className="w-5 h-5 shrink-0 text-[#131222] dark:text-neutral-400 mt-0.5"
                  aria-hidden
                />
                <a
                  href="mailto:ikbs8899@gmail.com"
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                >
                  ikbs8899@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin
                  className="w-5 h-5 shrink-0 text-[#131222] dark:text-neutral-400 mt-0.5"
                  aria-hidden
                />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=G-725%2FD%2C+Main+Road%2C+Sawantwadi%2C+Opp.+Renuka+Palace%2C+Sawantwadi%2C+Sindhudurg%2C+Maharashtra+%E2%80%93+416510"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors"
                >
                  G-725/D, Main Road, Sawantwadi, Opp. Renuka Palace, Sawantwadi, Sindhudurg, Maharashtra – 416510
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar - same grid as columns for alignment */}
        <div className="mt-10 pt-8 border-t border-[#FFB51D]/10 dark:border-neutral-800 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-12">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            &copy; {new Date().getFullYear()} I K TOURS AND TRAVELS. All rights reserved.
          </p>
          <div className="hidden lg:block lg:col-span-2" aria-hidden />
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Developed by{" "}
            <a
              href="https://www.ahmadwebsolutions.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors underline-offset-2"
            >
              Ahmad Web Solutions
            </a>
          </p>
        </div>
      </div>
    </SectionReveal>
  );
}

function SocialIcon({ name }: { name: string }) {
  const className = "w-5 h-5";
  switch (name) {
    case "facebook":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );
    case "twitter":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case "instagram":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.766 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
      );
    case "linkedin":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      );
    default:
      return null;
  }
}
